import React, { useState } from 'react'
import styled from 'styled-components'
import breakpoints from 'theme/breakpoints'
import support from 'theme/support'

import colors from 'theme/colors'
import Icon from 'components/_UI/Icon'
import Text from 'components/_UI/Text'
import Button from 'components/_UI/Button'
import { StyledRouteLink } from 'styled/UI/StyledButton'
import FeedbackForm from 'modules/users/form/FeedbackForm'
import Modal from 'components/_UI/Modal'
import { Column } from 'styled/alignment/Column'
import buttonText from 'texts/buttons/buttonText'
import sizes from 'theme/sizes'
import useToast from 'hooks/useToast'
import ToastContainer from 'components/_UI/ToastsContainer'
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';

export default function SupportContent() {
    const [openCards, setOpenCards] = useState(Array(support.faq.length).fill(false));
    const [openFeedbackForm, setOpenFeedbackForm] = useState(false)
    const [modalSucess, setModalSucess] = useState(false);

    const { toasts, toast } = useToast();

    function toggleCard(index) {
        const newOpenCards = [...openCards];
        newOpenCards[index] = !newOpenCards[index];
        setOpenCards(newOpenCards);
    }

    async function copyToClipboard(value) {
        if (navigator?.clipboard?.writeText) {
            try {
                await navigator.clipboard.writeText(value)
                toast.success('Copiado com sucesso!', { position: 'top-center' })
            } catch (error) {
                copyToClipboardFallback(value)
            }
        }
    }

    function copyToClipboardFallback(value) {
        const input = document.createElement('input');
        input.value = value;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
        toast.success('Copiado com sucesso!', { position: 'top-center' })
    }

    return (
        <PageContainer>
            <ToastContainer toasts={toasts} />

            <PageHeader>
                <HeaderAccent />
                <HeaderIcon>
                    <Icon name='suggestions' color={panelAccent(colors.occasionalPurple)} />
                </HeaderIcon>
                <HeaderText>
                    <PageTitle>Central de Suporte</PageTitle>
                    <PageDescription>
                        Estamos aqui para ajudar. Escolha um canal de atendimento ou encontre respostas nas perguntas frequentes.
                    </PageDescription>
                </HeaderText>
            </PageHeader>

            <FormSection>
                <SectionLabel>
                    <SectionIcon>
                        <Icon name='mail' color={panelAccent(colors.occasionalPurple)} />
                    </SectionIcon>
                    Canais de atendimento
                </SectionLabel>

                <CardsGrid>
                    <ContactCard>
                        <CardHeader>
                            <CardBadge>
                                <Icon name='mail' color={panelAccent(colors.occasionalPurple)} />
                            </CardBadge>
                            <CardInfo>
                                <CardTitle>E-mail</CardTitle>
                                <CardMeta>Resposta em até 24h</CardMeta>
                            </CardInfo>
                        </CardHeader>

                        <CardDescription>
                            Entre em contato através do e-mail <strong>{support.contact.email}</strong>
                        </CardDescription>

                        <CardActionButton onClick={() => copyToClipboard(support.contact.email)}>
                            Copiar E-mail
                        </CardActionButton>
                    </ContactCard>

                    <ContactCard>
                        <CardHeader>
                            <CardBadge color={`${colors.success}15`}>
                                <Icon name='whatsapp' color={colors.success} />
                            </CardBadge>
                            <CardInfo>
                                <CardTitle>Whatsapp</CardTitle>
                                <CardMeta>Atendimento em tempo real</CardMeta>
                            </CardInfo>
                        </CardHeader>

                        <CardDescription>
                            Entre em contato diretamente pelo nosso <strong>Whatsapp</strong>
                        </CardDescription>

                        <WhatsappLink to='https://wa.me/5391665255' target='_blank'>
                            Entrar em contato
                        </WhatsappLink>
                    </ContactCard>

                    <ContactCard>
                        <CardHeader>
                            <CardBadge color={`${panelAccentAlpha(colors.occasionalPurple, '12')}`}>
                                <Icon name='suggestions' color={panelAccent(colors.occasionalPurple)} />
                            </CardBadge>
                            <CardInfo>
                                <CardTitle>Sugestões</CardTitle>
                                <CardMeta>Ajude-nos a melhorar</CardMeta>
                            </CardInfo>
                        </CardHeader>

                        <CardDescription>
                            Envie <strong>sugestões</strong> e <strong>feedbacks</strong> para melhorarmos a plataforma
                        </CardDescription>

                        <CardActionButton onClick={() => setOpenFeedbackForm(true)}>
                            Enviar sugestões
                        </CardActionButton>
                    </ContactCard>
                </CardsGrid>
            </FormSection>

            <FormSection>
                <SectionLabel>
                    <SectionIcon>
                        <Icon name='suggestions' color={panelAccent(colors.occasionalPurple)} />
                    </SectionIcon>
                    Perguntas frequentes
                </SectionLabel>

                <FaqHint>
                    Respostas para as dúvidas mais comuns sobre eventos, inscrições e pagamentos.
                </FaqHint>

                <FaqList>
                    {support.faq.map((el, key) => (
                        <FaqItem key={key} active={openCards[key]}>
                            <FaqItemHeader onClick={() => toggleCard(key)} active={openCards[key]}>
                                <FaqNumber active={openCards[key]}>
                                    {String(key + 1).padStart(2, '0')}
                                </FaqNumber>
                                <FaqQuestion>{el.question}</FaqQuestion>
                                <FaqChevron active={openCards[key]} />
                            </FaqItemHeader>
                            <FaqContent active={openCards[key]}>
                                {el.response}
                            </FaqContent>
                        </FaqItem>
                    ))}
                </FaqList>
            </FormSection>

            <Modal isOpen={openFeedbackForm} onClose={() => setOpenFeedbackForm(false)}>
                <FeedbackForm onClose={() => setOpenFeedbackForm(false)} setModalSucess={setModalSucess} />
            </Modal>

            <Modal isOpen={modalSucess} onClose={() => setModalSucess(false)}>
                <Text name='default' type='bold'>Sugestões salvas com sucesso!</Text>
                <Column paddingRight='15%' paddingLeft='15%' marginTop={sizes.normal}>
                    <Button onClick={() => setModalSucess(false)}>
                        {buttonText.understood}
                    </Button>
                </Column>
            </Modal>
        </PageContainer>
    )
}

const PageContainer = styled.section`
    width: 100%;
    display: flex;
    flex-direction: column;
`;

const PageHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    background: ${colors.neutralLight};
    border: 1px solid ${colors.neutralDark};
    border-radius: 12px;
    padding: 24px 20px;
    margin-bottom: 16px;
    position: relative;
    overflow: hidden;

    @media screen and (min-width: ${breakpoints.md}) {
        padding: 28px;
    }
`;

const HeaderAccent = styled.div`
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: ${panelAccent(colors.occasionalPurple)};
    border-radius: 12px 0 0 12px;
`;

const HeaderIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: ${panelAccentAlpha(colors.occasionalPurple, '10')};
    flex-shrink: 0;
`;

const HeaderText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const PageTitle = styled.h1`
    font-size: 20px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin: 0;
`;

const PageDescription = styled.p`
    font-size: 14px;
    color: ${colors.night};
    margin: 0;
    line-height: 1.5;
`;

const FormSection = styled.div`
    background: ${colors.neutralLight};
    border: 1px solid ${colors.neutralDark};
    border-radius: 12px;
    padding: 24px 20px;
    margin-bottom: 16px;

    @media screen and (min-width: ${breakpoints.md}) {
        padding: 28px;
    }
`;

const SectionLabel = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin-bottom: 20px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
`;

const SectionIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #F3EAFF;
    flex-shrink: 0;
`;

const CardsGrid = styled.div`
    display: grid;
    gap: 12px;
    grid-template-columns: 1fr;

    @media screen and (min-width: ${breakpoints.md}) {
        grid-template-columns: repeat(3, 1fr);
    }
`;

const ContactCard = styled.div`
    border: 1.5px solid ${colors.night}22;
    border-radius: 12px;
    padding: 20px;
    background: ${colors.night}04;
    display: flex;
    flex-direction: column;
    gap: 14px;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:hover {
        border-color: ${colors.night}33;
        box-shadow: 0 2px 12px ${colors.night}0A;
    }
`;

const CardHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const CardBadge = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: ${({ color }) => color || `${panelAccentAlpha(colors.occasionalPurple, '15')}`};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

const CardInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const CardTitle = styled.span`
    font-size: 15px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const CardMeta = styled.span`
    font-size: 12px;
    color: ${colors.night};
`;

const CardDescription = styled.p`
    font-size: 13px;
    color: ${colors.night};
    margin: 0;
    line-height: 1.5;

    strong {
        color: ${colors.nightDark};
        font-weight: 600;
    }
`;

const CardActionButton = styled.button`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 10px 20px;
    border: none;
    border-radius: 10px;
    background: ${panelAccent(colors.occasionalPurple)};
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
        background: ${panelAccent(colors.secondaryPurple)};
    }
`;

const WhatsappLink = styled(StyledRouteLink)`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 10px 20px;
    border: none;
    border-radius: 10px;
    background: ${colors.success};
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
    box-shadow: none;
    text-decoration: none;

    &:hover {
        background: ${colors.successDark};
    }
`;

const FaqHint = styled.p`
    font-size: 13px;
    color: ${colors.night};
    margin: -8px 0 20px 0;
    line-height: 1.5;
`;

const FaqList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const FaqItem = styled.div`
    border: 1.5px solid ${({ active }) => active ? `${panelAccentAlpha(colors.occasionalPurple, '44')}` : `${colors.night}22`};
    border-radius: 12px;
    background: #fff;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    ${({ active }) => active && `
        box-shadow: 0 2px 12px ${panelAccentAlpha(colors.occasionalPurple, '12')};
    `}
`;

const FaqItemHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    cursor: pointer;
    transition: background 0.15s ease;

    ${({ active }) => active && `
        background: ${panelAccentAlpha(colors.occasionalPurple, '05')};
    `}

    &:hover {
        background: ${colors.night}08;
    }
`;

const FaqNumber = styled.div`
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: ${({ active }) => active ? `${panelAccentAlpha(colors.occasionalPurple, '15')}` : `${colors.night}11`};
    color: ${({ active }) => active ? panelAccent(colors.occasionalPurple) : colors.night};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 600;
    flex-shrink: 0;
    transition: all 0.2s ease;
`;

const FaqQuestion = styled.span`
    flex: 1;
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
    line-height: 1.4;
`;

const FaqChevron = styled.div`
    width: 10px;
    height: 10px;
    border-right: 2px solid ${({ active }) => active ? panelAccent(colors.occasionalPurple) : colors.nightLight};
    border-bottom: 2px solid ${({ active }) => active ? panelAccent(colors.occasionalPurple) : colors.nightLight};
    transform: ${({ active }) => active ? 'rotate(225deg)' : 'rotate(45deg)'};
    transition: all 0.25s ease;
    flex-shrink: 0;
    margin-right: 4px;
`;

const FaqContent = styled.div`
    overflow: hidden;
    height: 0;
    opacity: 0;
    transition: all 0.25s ease;
    font-size: 14px;
    color: ${colors.night};
    line-height: 1.7;

    ${({ active }) => active && `
        height: auto;
        opacity: 1;
        padding: 12px 16px 16px 60px;
    `}
`;
